import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { platform } from "@todesktop/client-core";

interface IContextProps {
  print(url: string): void;
}

// Electron's PDF viewer can't be printed programmatically (contentWindow.print()
// on a PDF frame is a silent no-op), so on desktop the PDF is rasterized to
// images and printed as a plain HTML document instead.
const pdfBlobToHtmlBlob = async (pdfBlob: Blob): Promise<Blob> => {
  // The webpack entry wires up the pdf.js worker as a bundled worker chunk;
  // it is only published as an .mjs file, so the extension is required. The
  // legacy build is needed because the modern one requires newer JS features
  // than the desktop app's Electron/Chromium provides.
  // eslint-disable-next-line import/extensions
  const pdfjs = await import("pdfjs-dist/legacy/webpack.mjs");
  const data = new Uint8Array(await pdfBlob.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pages = await Promise.all(
    Array.from({ length: pdf.numPages }, async (_, index) => {
      const page = await pdf.getPage(index + 1);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvas, viewport, intent: "print" }).promise;
      return `<img src="${canvas.toDataURL("image/png")}" />`;
    }),
  );
  await pdf.loadingTask.destroy();
  const html = `<!DOCTYPE html><html><head><style>
    body { margin: 0; }
    img { display: block; width: 100%; break-after: page; }
    img:last-child { break-after: auto; }
  </style></head><body>${pages.join("")}</body></html>`;
  return new Blob([html], { type: "text/html" });
};

const PrintContext = createContext<IContextProps>({
  print: () => {},
});

export interface IProviderProps {
  children?: ReactNode;
}

export const PrintProvider = ({ children }: IProviderProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [url, setUrl] = useState<string>();

  const print = useCallback((url: string) => {
    axios.get<Blob>(url, { responseType: "blob" }).then(async (response) => {
      const blob = platform.todesktop.isDesktopApp()
        ? await pdfBlobToHtmlBlob(response.data)
        : response.data;
      setUrl(URL.createObjectURL(blob));
    });
  }, []);

  const value = useMemo<IContextProps>(
    () => ({
      print,
    }),
    [print],
  );

  return (
    <PrintContext.Provider value={value}>
      <iframe
        ref={iframeRef}
        title="pdf"
        src={url}
        style={{ display: "none" }}
        onLoad={() => {
          // Clearing the url navigates the iframe to about:blank, which fires
          // onLoad again; skip that pass so we don't reopen the print dialog.
          if (url && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.print();
            iframeRef.current.contentWindow.onafterprint = () => {
              setUrl(undefined);
            };
          }
        }}
      />
      {children}
    </PrintContext.Provider>
  );
};

export const usePrint = () => useContext(PrintContext);
